import { DeleteIcon, CopyIcon } from '@chakra-ui/icons'
import {
  Button,
  Divider,
  Spacer,
  Text as ChakraText,
  HStack,
  VStack,
  useClipboard,
  Tooltip,
} from '@chakra-ui/react'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'

import { UserContext } from '../../providers'
import { MainContentHeadingBox } from '../../layouts/Base/MainContent'
import type { UrlEndpointsResponse } from '../../interfaces'
import PermissionGuard from '../PermissionGuard'
import { PermissionsEnum } from '../../../utils'

const ActionBar = ({
  urlEndpoint,
  isSubmitting,
  onOpenDelete,
  resetForm,
  dirty,
  isFormValid,
}: {
  dirty: boolean
  urlEndpoint?: UrlEndpointsResponse
  isSubmitting: boolean
  isFormValid: boolean
  onOpenDelete(): void
  resetForm(): void
}): React.ReactElement => {
  const { user } = useContext(UserContext)
  const { t } = useTranslation()
  const toast = useToast()
  const { onCopy } = useClipboard(
    `${user?.baseUrlForClientImageFetch}/${urlEndpoint?.urlPrefix}`,
  )

  return (
    <MainContentHeadingBox>
      {urlEndpoint ? (
        <VStack width="70%" align="start">
          <HStack width="full" spacing={0.5}>
            <Tooltip
              label={`${user?.baseUrlForClientImageFetch}/${urlEndpoint.urlPrefix}`}
            >
              <ChakraText
                mr="0.5em"
                fontSize="lg"
                fontWeight="semibold"
                color={colors.appDarkIndigo}
                isTruncated
              >
                {`${user?.baseUrlForClientImageFetch}/${urlEndpoint.urlPrefix}`}
              </ChakraText>
            </Tooltip>

            <CopyIcon
              onClick={() => {
                onCopy()
                toast({
                  description: t('copied', 'Copied!'),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              }}
              cursor="pointer"
            />
          </HStack>
          <Tooltip label={urlEndpoint.desc}>
            <ChakraText
              width="full"
              fontSize="md"
              color={colors.appSlate}
              isTruncated
            >
              {urlEndpoint.desc}
            </ChakraText>
          </Tooltip>
        </VStack>
      ) : (
        <ChakraText
          color={colors.appDarkIndigo}
          fontWeight="semibold"
          fontSize="18px"
        >
          {t('add_new_endpoint', 'Add New URL Endpoint')}
        </ChakraText>
      )}
      <Spacer />

      <PermissionGuard
        permission={PermissionsEnum.SELF_INTEGRATION_MANAGE}
      >
        {
          (hasPermission: boolean) => (
            hasPermission ? (
              <HStack spacing={4} h="full">
                {urlEndpoint && !urlEndpoint.isDefault &&  (
                  <Button
                    bgSize="sm"
                    leftIcon={<DeleteIcon />}
                    variant="deleteCTA"
                    onClick={onOpenDelete}
                  >
                    {t('delete', 'Delete')}
                  </Button>
                )}
                {dirty && (
                  <Button variant="outline" marginLeft="1em" onClick={resetForm}>
                    {t('cancel', 'Cancel')}
                  </Button>
                )}
                <Button
                  type="submit"
                  isDisabled={!isFormValid || !dirty}
                  isLoading={isSubmitting}
                  loadingText="Saving"
                >
                  {t('save', 'Save')}
                </Button>
              </HStack>
            ) : <></>
          )
        }
      </PermissionGuard>
    </MainContentHeadingBox>
  )
}

export default ActionBar
